<template>
    <div ref="rootEl" class="px-4 py-4">
        <div class="d-flex flex-column ga-2 mb-4">
            <div class="text-body-2 text-medium-emphasis">Effective date: January 16, 2026</div>
            <div class="text-body-2">
                <div><strong>Company:</strong> HOUSE OF OTTO LLC (Arizona)</div>
                <div><strong>Product:</strong> Mix n’ Batch</div>
                <div><strong>Support:</strong> support@mixnbatch.app</div>
            </div>
        </div>

        <div class="d-flex flex-column ga-10">
            <!-- 1 -->
            <section id="simple-truth" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">1) The simple truth</div>
                <p class="text-body-1">We built Mix n’ Batch so you can run your business, not so we can monetize your data.</p>
                <p class="text-body-1 mb-0">We do not sell your personal data. We never will.</p>
            </section>

            <!-- 2 -->
            <section id="what-we-collect" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">2) What we collect</div>

                <div class="text-subtitle-1 font-weight-bold mt-2 mb-2">Information you provide</div>
                <p class="text-body-1">When you sign up or use the app, we may collect:</p>
                <ul class="mb-4 pl-6">
                    <li class="text-body-1 mb-1">Name</li>
                    <li class="text-body-1 mb-1">Email address</li>
                    <li class="text-body-1 mb-1">Business name</li>
                    <li class="text-body-1 mb-1">Type of business</li>
                    <li class="text-body-1 mb-1">
                        Your Content you store in the app (recipes, ingredients, costs, pricing, orders/customers, invoices, uploads like
                        labels/receipts, notes, and other app data)
                    </li>
                </ul>

                <div class="text-subtitle-1 font-weight-bold mt-2 mb-2">Information collected automatically (basic operational data)</div>
                <p class="text-body-1">Like most apps, we may collect limited technical data needed for security and reliability, such as:</p>
                <ul class="mb-4 pl-6">
                    <li class="text-body-1 mb-1">IP address (often)</li>
                    <li class="text-body-1 mb-1">device/browser information</li>
                    <li class="text-body-1 mb-1">log data related to errors or performance</li>
                </ul>
                <p class="text-body-1 mb-0">We try to keep this minimal.</p>
            </section>

            <!-- 3 -->
            <section id="payments" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">3) Payments (Stripe)</div>
                <p class="text-body-1">Payments are processed by Stripe using Stripe Payment Elements.</p>
                <p class="text-body-1">We do not store your full card number on our servers.</p>
                <p class="text-body-1">We can’t see your full card details.</p>
                <p class="text-body-1 mb-0">Stripe will process payment information under their own privacy policy.</p>
            </section>

            <!-- 4 -->
            <section id="how-we-use" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">4) How we use information</div>
                <p class="text-body-1">We use data only to operate and improve Mix n’ Batch, including to:</p>
                <ul class="mb-0 pl-6">
                    <li class="text-body-1 mb-1">create and manage your account</li>
                    <li class="text-body-1 mb-1">provide the app’s features and save your data</li>
                    <li class="text-body-1 mb-1">send transactional emails (like verification, password reset, billing, service notices)</li>
                    <li class="text-body-1 mb-1">provide support when you contact us</li>
                    <li class="text-body-1 mb-1">keep the service secure and reliable (including fixing bugs)</li>
                </ul>
            </section>

            <!-- 5 -->
            <section id="email" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">5) Email (Resend) and link tracking</div>
                <p class="text-body-1">We send transactional emails through Resend.</p>
                <p class="text-body-1">
                    Some transactional emails may include click tracking (so we can tell whether links were used). We do not use this for ad
                    targeting.
                </p>
                <p class="text-body-1">If we add marketing emails in the future:</p>
                <ul class="mb-0 pl-6">
                    <li class="text-body-1 mb-1">they will be opt-in</li>
                    <li class="text-body-1 mb-1">you’ll be able to unsubscribe anytime</li>
                </ul>
            </section>

            <!-- 6 -->
            <section id="sentry" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">6) Error monitoring (Sentry)</div>
                <p class="text-body-1">We use Sentry to detect errors and improve stability.</p>
                <p class="text-body-1">Sentry may receive:</p>
                <ul class="mb-4 pl-6">
                    <li class="text-body-1 mb-1">error logs and diagnostics</li>
                    <li class="text-body-1 mb-1">device/browser info</li>
                    <li class="text-body-1 mb-1">sometimes parts of a request associated with an error</li>
                </ul>
                <p class="text-body-1 mb-0">
                    We do not intentionally send sensitive personal content to Sentry, but logs can sometimes include data depending on what happened
                    (for example, a failing request). We aim to minimize that.
                </p>
            </section>

            <!-- 7 -->
            <section id="hosting" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">7) Hosting (DigitalOcean) and storage</div>
                <p class="text-body-1">Mix n’ Batch is hosted on DigitalOcean. Your data is stored on infrastructure used to run the service.</p>
                <p class="text-body-1 mb-0">We use reasonable safeguards to protect your data, but no system is perfect.</p>
            </section>

            <!-- 8 -->
            <section id="sharing" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">8) When we share data</div>
                <p class="text-body-1">We share data only when needed to operate the service, including with:</p>
                <ul class="mb-4 pl-6">
                    <li class="text-body-1 mb-1">service providers (like Stripe, Resend, Sentry, DigitalOcean)</li>
                    <li class="text-body-1 mb-1">legal compliance (if required by law)</li>
                    <li class="text-body-1 mb-1">security and fraud prevention</li>
                </ul>
                <p class="text-body-1 mb-0">We do not sell your data.</p>
            </section>

            <!-- 9 -->
            <section id="security" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">9) Security</div>
                <p class="text-body-1">We take security seriously and use reasonable measures to protect data.</p>
                <p class="text-body-1 mb-0">If you believe you found a security issue, please email support@mixnbatch.app.</p>
            </section>

            <!-- 10 -->
            <section id="retention" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">10) Data retention</div>
                <p class="text-body-1">We keep data for as long as your account is active and as needed to provide the service.</p>
                <p class="text-body-1 mb-0">
                    If you request deletion, we’ll delete your account and associated data within our operational timelines.
                </p>
            </section>

            <!-- 11 -->
            <section id="your-rights" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">11) Your rights and choices</div>
                <p class="text-body-1">You can update account info inside the app. You can also request help with access or deletion via support.</p>
                <p class="text-body-1 mb-0">We’ll respond in a reasonable timeframe.</p>
            </section>

            <!-- 12 -->
            <section id="children" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">12) Children</div>
                <p class="text-body-1 mb-0">
                    Mix n’ Batch is not intended for children under 13. We do not knowingly collect data from children under 13.
                </p>
            </section>

            <!-- 13 -->
            <section id="changes" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">13) Changes to this policy</div>
                <p class="text-body-1 mb-0">
                    If we change this Privacy Policy, we’ll update the effective date and may notify you in-app or by email for meaningful changes.
                </p>
            </section>

            <!-- 14 -->
            <section id="contact" class="legal-section">
                <div class="text-h6 font-weight-bold mb-2">14) Contact</div>
                <p class="text-body-1 mb-0">Privacy questions? Email support@mixnbatch.app.</p>
            </section>
        </div>

        <div class="d-flex mt-6 justify-end">
            <v-btn variant="text" @click="scrollToTop">Back to top</v-btn>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { scrollToTopFromElement } from '@/utils/scrollToTopFromElement';
    import { ref } from 'vue';

    const rootEl = ref<HTMLElement | null>(null);

    const scrollToTop = () => {
        scrollToTopFromElement(rootEl.value);
    };
</script>

<style lang="scss" scoped>
    .legal-section {
        scroll-margin-top: 24px;
    }
</style>
